/**
 * DJH · physical-scale renderer for every heirloom width shown on a page.
 *
 * Generic by design: it draws anything carrying `data-djh-hw-bar="<mm>"`, so the width ladder,
 * the side-by-side comparator and the sizing guide share one implementation and one calibration.
 * Adding a width to a page is adding an element, never adding script.
 *
 * WHY THIS IS THE PIECE THAT MATTERS. Dianna named the number-one customer regret as "not getting
 * the millimeter/width/length they initially wanted". Whatever the eventual top-level sort turns
 * out to be — Henry has re-opened that question — a shopper who has physically seen 8 mm beside
 * 20 mm does not make that mistake. So this stays useful in every arrangement of the browse.
 *
 * No pricing here on purpose: a heirloom price needs six more axes, and a number quoted from
 * width alone is a number we would have to take back at the configurator.
 */
(function () {
  /**
   * Say that it worked.
   *
   * "A control that appears to do nothing reads as broken" — which is what happened:
   * the bands DID rescale (measured: a 12 mm band moves 81.6px to 108px) and nothing
   * on the page said so, so the feature read as inert. aria-live carries it to a
   * screen reader as well, since a purely visual confirmation of a purely visual
   * change helps nobody who cannot see either.
   */
  function announce(root) {
    const out = root.querySelector('[data-djh-hw-cal-result]');
    if (!out) return;
    out.textContent = out.dataset.djhDone || out.getAttribute('data-done') || '';
  }

  const roots = document.querySelectorAll('[data-djh-hw]');
  const mm = window.DJHmmScale;
  if (!roots.length || !mm) return;

  function draw() {
    const scale = mm.pxPerMm();

    document.querySelectorAll('[data-djh-hw-bar]').forEach((bar) => {
      const w = parseFloat(bar.dataset.djhHwBar);
      if (Number.isFinite(w)) bar.style.height = w * scale + 'px';
    });
    document.querySelectorAll('[data-djh-hw-coin]').forEach((coin) => {
      coin.style.width = mm.QUARTER_MM * scale + 'px';
      coin.style.height = mm.QUARTER_MM * scale + 'px';
    });
    document.querySelectorAll('[data-djh-hw-card]').forEach((card) => {
      card.style.width = mm.CARD_MM * scale + 'px';
    });

    // A RULER, drawn in millimeter coordinates.
    //
    // The sizing guide tells someone to wrap a strip, mark the overlap, lay it flat
    // and read the millimeters — then look the number up in a table where 54.4 mm is
    // a size 7 and 57.0 mm is a size 8. Two and a half millimeters decide the ring.
    //
    // So a ruler on that page is not decoration, it is the instrument. Drawing it at
    // a nominal scale would teach someone to trust a ruler that is not one, and the
    // table would turn a bad reading into a wrong ring.
    //
    // The SVG's viewBox is in MILLIMETERS, so every tick sits at an integer mm by
    // construction and only the outer width needs scaling. There is no second place
    // for the tick spacing to disagree with the label.
    document.querySelectorAll('[data-djh-hw-ruler]').forEach((ruler) => {
      const mmWide = parseFloat(ruler.dataset.djhHwRuler);
      if (Number.isFinite(mmWide)) ruler.style.width = mmWide * scale + 'px';
    });
    document.querySelectorAll('[data-djh-hw-claim]').forEach((el) => {
      el.textContent = mm.isCalibrated()
        ? el.dataset.djhHwClaimCalibrated || ''
        : el.dataset.djhHwClaimUncalibrated || '';
    });
  }

  roots.forEach((root) => {
    const cal = root.querySelector('[data-djh-hw-cal]');
    if (cal) {
      cal.value = String(mm.pxPerMm());
      cal.addEventListener('input', () => {
        mm.set(cal.value);
        announce(root);
      });
    }

    // ── The card IS the control ───────────────────────────────────────────────────
    //
    // Henry: "it's not clear what the slider function is for. we slide back and forth
    // and it doesn't apply to a size." The copy had always described dragging a card;
    // the control was a slider somewhere else. Direct manipulation closes that gap —
    // grab the card's edge, stretch it to your own, and the whole page redraws.
    //
    // Pointer events rather than mouse events, so this works with a finger on the iPad
    // the 1 Oct demo runs on. `touch-action: none` on the grip stops the browser
    // scrolling the page instead of dragging.
    //
    // The slider stays and is not decoration: a drag handle cannot be operated by
    // keyboard, and calibration must not become a mouse-and-touch-only feature.
    const card = root.querySelector('[data-djh-hw-card]');
    const grip = root.querySelector('[data-djh-hw-card-grip]');
    if (card && grip) {
      let startX = 0;
      let startWidth = 0;

      // State is an explicit flag, NOT pointer capture. Gating the move on
      // hasPointerCapture() made the drag depend on capture succeeding — and capture
      // can be lost mid-gesture, silently ending a drag the user is still performing.
      // Capture is requested as an enhancement and its failure is not fatal.
      let dragging = false;

      grip.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        dragging = true;
        startX = e.clientX;
        startWidth = card.getBoundingClientRect().width;
        try { grip.setPointerCapture(e.pointerId); } catch (_) { /* enhancement only */ }
      });

      // Listening on the document, not the grip: a fast drag outruns a 28px handle,
      // and without capture the pointer would leave it and the card would stop
      // following the finger halfway through.
      document.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const width = startWidth + (e.clientX - startX);
        // Clamped to the same range the slider allows, so the two controls cannot
        // disagree about what is reachable.
        const perMm = Math.min(9, Math.max(2, width / mm.CARD_MM));
        mm.set(perMm);
        if (cal) cal.value = String(perMm);
        announce(root);
      });

      const release = (e) => {
        dragging = false;
        try {
          if (grip.hasPointerCapture(e.pointerId)) grip.releasePointerCapture(e.pointerId);
        } catch (_) { /* nothing to release */ }
      };
      document.addEventListener('pointerup', release);
      document.addEventListener('pointercancel', release);
    }
    const reset = root.querySelector('[data-djh-hw-cal-reset]');
    if (reset) {
      reset.addEventListener('click', () => {
        mm.reset();
        if (cal) cal.value = String(mm.CSS_PX_PER_MM);
        const out = root.querySelector('[data-djh-hw-cal-result]');
        if (out) out.textContent = '';
      });
    }
  });

  mm.onChange(draw);
  draw();
})();
